import type { Contact } from "./domain";

export interface CardCandidateFields {
  displayName: string;
  firstName: string;
  lastName: string;
  chineseName: string;
  company: string;
  department: string;
  title: string;
  emails: string[];
  mobiles: string[];
  telephones: string[];
  website: string;
  linkedinUrl: string;
  wechatId: string;
  notes: string;
  street: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  connectedDate: string;
  cardImagePaths: string[];
}

const normalizeEmail = (value: string) => value.trim().toLocaleLowerCase("en");
const normalizePhone = (value: string) => value.trim().replace(/^00/, "+").replace(/[^+\d]/g, "");
const normalizeLinkedIn = (value: string) => value.trim().toLocaleLowerCase("en")
  .replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");

export function findExactContact(candidate: CardCandidateFields, contacts: Contact[]): Contact | null {
  const emails = new Set(candidate.emails.map(normalizeEmail).filter(Boolean));
  const phones = new Set([...candidate.mobiles, ...candidate.telephones].map(normalizePhone).filter(Boolean));
  const linkedIn = normalizeLinkedIn(candidate.linkedinUrl);
  return contacts.find((contact) => {
    const emailMatch = contact.emails.some((value) => emails.has(normalizeEmail(value)));
    const phoneMatch = [...contact.mobiles, ...contact.telephones].some((value) => phones.has(normalizePhone(value)));
    const linkedInMatch = Boolean(linkedIn) && normalizeLinkedIn(contact.linkedinUrl) === linkedIn;
    return emailMatch || phoneMatch || linkedInMatch;
  }) ?? null;
}

export function cardCandidateContactData(candidate: CardCandidateFields) {
  const connected = candidate.connectedDate.slice(0, 10);
  return {
    displayName: candidate.displayName.trim(), firstName: candidate.firstName.trim(), lastName: candidate.lastName.trim(), chineseName: candidate.chineseName.trim(),
    company: candidate.company.trim(), company2: "", companyOthers: "", department: candidate.department.trim(), title: candidate.title.trim(), title2: "", titleOthers: "",
    emails: candidate.emails.map((value) => value.trim()).filter(Boolean), mobiles: candidate.mobiles.map((value) => value.trim()).filter(Boolean), telephones: candidate.telephones.map((value) => value.trim()).filter(Boolean),
    website: candidate.website.trim(), linkedinUrl: candidate.linkedinUrl.trim(), wechatId: candidate.wechatId.trim(), notes: candidate.notes.trim(),
    street: candidate.street.trim(), city: candidate.city.trim(), state: candidate.state.trim(), country: candidate.country.trim(), zip: candidate.zip.trim(),
    cameraConnectedOn: connected, cardImagePaths: candidate.cardImagePaths, cardImagePath: candidate.cardImagePaths[0] || "", cardImageBackPaths: candidate.cardImagePaths.slice(1, 2), cardImageBackPath: candidate.cardImagePaths[1] || "",
  };
}

export function nonEmptyCardUpdate(data: ReturnType<typeof cardCandidateContactData>) {
  return Object.fromEntries(Object.entries(data).filter(([key, value]) => {
    if (["company2", "companyOthers", "title2", "titleOthers"].includes(key)) return false;
    return Array.isArray(value) ? value.length > 0 : String(value).trim().length > 0;
  }));
}
